import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Wallet, Plus, Trash2, Pencil, Filter, User, Calendar, Euro, CheckCircle2,
  Clock, FileText, XCircle, TrendingUp, ChevronDown, ExternalLink, Users,
  Download, FileDown,
} from 'lucide-react';

import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const STATUS_META = {
  pending:   { label: 'In attesa',  className: 'bg-amber-100 text-amber-800 border-amber-200',   icon: Clock },
  unlocked:  { label: 'Sbloccata',  className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  invoiced:  { label: 'Fatturata',  className: 'bg-blue-100 text-blue-800 border-blue-200',       icon: FileText },
  paid:      { label: 'Incassata',  className: 'bg-violet-100 text-violet-800 border-violet-200', icon: Euro },
  cancelled: { label: 'Annullata',  className: 'bg-slate-100 text-slate-600 border-slate-200',    icon: XCircle },
};

const fmtEUR = (v) => new Intl.NumberFormat('it-IT', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(v || 0));

const shortDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

const isAdminEmail = (email) => [
  'legal@housecashflows.com',
  'eufemia@housecashflows.com',
  'morena@housecashflows.com',
  'claudia@housecashflows.com',
].includes((email || '').toLowerCase());

export default function AgentCommissionsAdminPage() {
  const { user } = useAuth();
  const canAccess = isAdminEmail(user?.email);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [byStatus, setByStatus] = useState({});
  const [loading, setLoading] = useState(false);

  // Filtri
  const [filterAgentId, setFilterAgentId] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');

  // Modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Data sources per dropdown
  const [agents, setAgents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [levels, setLevels] = useState([]);

  const load = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const params = {};
      if (filterAgentId !== 'all') params.agent_id = filterAgentId;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterLevel !== 'all') params.level = filterLevel;
      const { data } = await api.get('/admin/agent-commissions', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setByStatus(data.by_status || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento commissioni');
    } finally {
      setLoading(false);
    }
  }, [canAccess, filterAgentId, filterStatus, filterLevel]);

  const loadDropdowns = useCallback(async () => {
    if (!canAccess) return;
    try {
      const [a, p, l] = await Promise.all([
        api.get('/admin/agent-commissions/agents-list'),
        api.get('/admin/agent-commissions/proposal-payments-list', { params: { limit: 200 } }),
        api.get('/admin/agent-commissions/levels-list'),
      ]);
      setAgents(a.data.items || []);
      setPayments(p.data.items || []);
      setLevels(l.data.items || []);
    } catch (e) {
      /* silent */
    }
  }, [canAccess]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDropdowns(); }, [loadDropdowns]);

  const onStatusChange = async (row, newStatus) => {
    try {
      await api.patch(`/admin/agent-commissions/${row.id}`, { status: newStatus });
      toast.success(`Stato aggiornato: ${STATUS_META[newStatus]?.label || newStatus}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const onDeleteConfirmed = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/admin/agent-commissions/${deleting.id}`);
      toast.success('Commissione eliminata');
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  if (!canAccess) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="font-serif text-2xl mb-2">Accesso negato</h1>
          <p className="text-sm text-muted-foreground">
            Solo Legal, Eufemia, Morena e Claudia possono accedere a questa pagina.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="agent-commissions-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">CRM · Compensi</div>
          <h1 className="font-serif text-2xl md:text-3xl inline-flex items-center gap-2">
            <Wallet className="w-6 h-6 text-[var(--gp-gold)]" />
            Commissioni Agente
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Crea, gestisci e distribuisci le commissioni agli agenti. Ogni voce è agganciata a una chiusura reale.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons
            variant="admin"
            filters={{
              ...(filterAgentId !== 'all' && { agent_id: filterAgentId }),
              ...(filterStatus !== 'all' && { status: filterStatus }),
            }}
          />
          <Button onClick={() => setCreateOpen(true)} data-testid="btn-create-commission" className="h-10">
            <Plus className="w-4 h-4 mr-1.5" />
            Nuova commissione
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-3 text-center">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Totale filtrato</div>
          <div className="font-serif text-2xl text-[hsl(var(--primary))] mt-1 tabular-nums" data-testid="kpi-total">{fmtEUR(total)}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{items.length} righe</div>
        </Card>
        {Object.entries(STATUS_META).map(([key, meta]) => {
          const val = byStatus[key] || 0;
          const Icon = meta.icon;
          return (
            <Card key={key} className="p-3 text-center" data-testid={`kpi-${key}`}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1 justify-center">
                <Icon className="w-3 h-3" />
                {meta.label}
              </div>
              <div className="font-serif text-lg mt-1 tabular-nums">{fmtEUR(val)}</div>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Livello</Label>
            <Select value={filterLevel} onValueChange={setFilterLevel}>
              <SelectTrigger data-testid="filter-level" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i livelli</SelectItem>
                {levels.map((l) => (
                  <SelectItem key={l.level} value={l.level}>
                    {l.label} <span className="text-muted-foreground ml-1">({l.count})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Agente</Label>
            <Select value={filterAgentId} onValueChange={setFilterAgentId}>
              <SelectTrigger data-testid="filter-agent" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli agenti</SelectItem>
                {agents
                  .filter((a) => filterLevel === 'all' || a.level === filterLevel)
                  .map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                      {a.level_label && (
                        <span className="text-muted-foreground ml-1">· {a.level_label}</span>
                      )}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Stato</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={() => { setFilterAgentId('all'); setFilterStatus('all'); setFilterLevel('all'); }} className="h-10">
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            Azzera filtri
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="commissions-table">
            <thead className="bg-[var(--surface-2)]/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left py-2.5 px-3">Chiusura</th>
                <th className="text-left py-2.5 px-3">Agente</th>
                <th className="text-right py-2.5 px-3">Importo</th>
                <th className="text-center py-2.5 px-3">Sblocco</th>
                <th className="text-center py-2.5 px-3">Stato</th>
                <th className="py-2.5 px-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Caricamento…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center">
                    <Wallet className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <div className="text-sm text-muted-foreground">Nessuna commissione trovata.</div>
                    <Button size="sm" variant="outline" className="mt-3" onClick={() => setCreateOpen(true)}>
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Crea la prima
                    </Button>
                  </td>
                </tr>
              )}
              {items.map((row) => {
                const s = STATUS_META[row.status] || STATUS_META.pending;
                const StatusIcon = s.icon;
                const pp = row.proposal_payment || {};
                return (
                  <tr key={row.id} className="border-t border-border hover:bg-[var(--surface-2)]/30 transition-colors" data-testid={`row-${row.id}`}>
                    <td className="py-3 px-3">
                      <div className="font-medium">{pp.lead_name || '—'}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <span>{fmtEUR(pp.amount_eur || 0)}</span>
                        {pp.resort_slug && <Badge variant="outline" className="text-[10px] py-0 h-4">{pp.resort_slug}</Badge>}
                        {pp.lead_id && (
                          <Link to={`/app/leads/${pp.lead_id}`} className="text-[hsl(var(--primary))] hover:underline inline-flex items-center">
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="font-medium">{row.agent_name || '—'}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <span>{row.agent_email}</span>
                        {row.agent_level_label && (
                          <Badge variant="outline" className="text-[10px] py-0 h-4">{row.agent_level_label}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-right tabular-nums">
                      <div className="font-medium">{fmtEUR(row.amount)}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {row.amount_type === 'percent' ? `${row.amount_percent}% di ${fmtEUR(pp.amount_eur || 0)}` : 'Fisso'}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="inline-flex items-center gap-1 text-sm">
                        <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                        {shortDate(row.unlock_date)}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <Select value={row.status} onValueChange={(v) => onStatusChange(row, v)}>
                        <SelectTrigger className={`h-7 text-xs w-[130px] ${s.className} border`} data-testid={`status-${row.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_META).map(([k, m]) => (
                            <SelectItem key={k} value={k} className="text-xs">
                              <m.icon className="w-3 h-3 inline mr-1" />
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(row)} data-testid={`edit-${row.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleting(row)} data-testid={`del-${row.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* CREATE / EDIT MODAL */}
      <CommissionModal
        open={createOpen || !!editing}
        editing={editing}
        agents={agents}
        payments={payments}
        onClose={() => { setCreateOpen(false); setEditing(null); }}
        onSaved={() => { setCreateOpen(false); setEditing(null); load(); }}
      />

      {/* DELETE CONFIRM */}
      <AlertDialog open={!!deleting} onOpenChange={(v) => { if (!v) setDeleting(null); }}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la commissione?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && (
                <span>
                  Commissione di <b>{fmtEUR(deleting.amount)}</b> per <b>{deleting.agent_name}</b> collegata a <b>{deleting.proposal_payment?.lead_name}</b>. Operazione irreversibile.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onDeleteConfirmed} data-testid="confirm-delete-btn">
              Sì, elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Modal create/edit ─────────────────────────────────────────────────────
// ★ Round 2 qualità — ogni riga ha un uid stabile (niente key={index}: React
// non perde lo stato dei campi quando si rimuove una riga intermedia).
let rowUidCounter = 0;
const newCommissionRow = (patch = {}) => ({
  _uid: `crow-${Date.now()}-${rowUidCounter += 1}`,
  agent_id: '',
  amount_type: 'fixed',
  amount_fixed: '',
  amount_percent: '',
  unlock_date: '',
  notes: '',
  ...patch,
});

function CommissionModal({ open, editing, agents, payments, onClose, onSaved }) {
  const isEdit = !!editing;
  // Split rows: for CREATE, we allow multiple agent+amount rows on same PP
  const [ppId, setPpId] = useState('');
  const [rows, setRows] = useState([newCommissionRow()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setPpId(editing.proposal_payment_id);
      setRows([newCommissionRow({
        agent_id: editing.agent_id,
        amount_type: editing.amount_type || 'fixed',
        amount_fixed: editing.amount_fixed ?? '',
        amount_percent: editing.amount_percent ?? '',
        unlock_date: (editing.unlock_date || '').slice(0, 10),
        notes: editing.notes || '',
      })]);
    } else {
      setPpId('');
      setRows([newCommissionRow()]);
    }
  }, [open, editing]);

  const pp = useMemo(() => payments.find((p) => p.id === ppId), [payments, ppId]);
  const paidAmount = pp?.amount_eur || 0;

  const updateRow = (i, patch) => setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows((prev) => [...prev, newCommissionRow()]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const previewAmount = (r) => {
    if (r.amount_type === 'percent') {
      const pct = Number(r.amount_percent || 0);
      return (paidAmount * pct) / 100;
    }
    return Number(r.amount_fixed || 0);
  };

  const canSubmit = ppId && rows.every((r) => r.agent_id && r.unlock_date && (
    (r.amount_type === 'fixed' && Number(r.amount_fixed) > 0) ||
    (r.amount_type === 'percent' && Number(r.amount_percent) > 0)
  ));

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      if (isEdit) {
        const r = rows[0];
        await api.patch(`/admin/agent-commissions/${editing.id}`, {
          amount_type: r.amount_type,
          amount_fixed: r.amount_type === 'fixed' ? Number(r.amount_fixed) : null,
          amount_percent: r.amount_type === 'percent' ? Number(r.amount_percent) : null,
          unlock_date: r.unlock_date,
          notes: r.notes,
        });
        toast.success('Commissione aggiornata');
      } else {
        // Create N rows (split multi-agente)
        for (const r of rows) {
          await api.post('/admin/agent-commissions', {
            proposal_payment_id: ppId,
            agent_id: r.agent_id,
            amount_type: r.amount_type,
            amount_fixed: r.amount_type === 'fixed' ? Number(r.amount_fixed) : null,
            amount_percent: r.amount_type === 'percent' ? Number(r.amount_percent) : null,
            unlock_date: r.unlock_date,
            notes: r.notes,
          });
        }
        toast.success(rows.length === 1 ? 'Commissione creata' : `${rows.length} commissioni create (split)`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="commission-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[var(--gp-gold)]" />
            {isEdit ? 'Modifica commissione' : 'Nuova commissione'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Aggiorna importo, data di sblocco o note.'
              : 'Seleziona la chiusura, poi assegna una o più commissioni (split multi-agente).'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Chiusura */}
          <div>
            <Label className="text-xs uppercase tracking-widest">Chiusura</Label>
            <Select value={ppId} onValueChange={setPpId} disabled={isEdit}>
              <SelectTrigger data-testid="select-pp" className="mt-1">
                <SelectValue placeholder="Seleziona una chiusura…" />
              </SelectTrigger>
              <SelectContent>
                {payments.length === 0 && <SelectItem value="__none" disabled>Nessuna chiusura disponibile</SelectItem>}
                {payments.map((p) => {
                  const srcBadge = p.source === 'order'
                    ? '· Proposta'
                    : p.source === 'lead'
                    ? '· Lead chiuso'
                    : '· Pagamento';
                  return (
                    <SelectItem key={p.id} value={p.id}>
                      {p.lead_name || 'Lead'} · {fmtEUR(p.amount_eur || 0)} · {shortDate(p.paid_at)}
                      <span className="text-muted-foreground ml-1 text-[10px]">{srcBadge}</span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {pp && (
              <div className="mt-2 text-xs text-muted-foreground bg-[var(--surface-2)]/50 rounded-md p-2 border">
                Chiusura di <b>{fmtEUR(paidAmount)}</b> · Lead: {pp.lead_name} · Data: {shortDate(pp.paid_at)}
                {pp.source && pp.source !== 'payment' && (
                  <div className="text-[11px] mt-1 text-[hsl(var(--primary))]">
                    ℹ️ Sorgente: {pp.source === 'order' ? 'Proposta commerciale' : 'Lead chiuso'} — al salvataggio verrà creata una nuova voce pagamento collegata.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Rows (agent split) */}
          <div className="space-y-3">
            {rows.map((r, i) => (
              <div key={r._uid} className="rounded-lg border p-3 bg-[var(--surface-1)] space-y-3" data-testid={`row-form-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    {isEdit ? 'Agente e importo' : `Commissione ${i + 1}${rows.length > 1 ? ` di ${rows.length}` : ''}`}
                  </div>
                  {!isEdit && rows.length > 1 && (
                    <Button variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => removeRow(i)}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Rimuovi
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Agente</Label>
                    <Select value={r.agent_id} onValueChange={(v) => updateRow(i, { agent_id: v })} disabled={isEdit}>
                      <SelectTrigger data-testid={`select-agent-${i}`} className="mt-1">
                        <SelectValue placeholder="Seleziona agente…" />
                      </SelectTrigger>
                      <SelectContent>
                        {agents.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name} · <span className="text-muted-foreground">{a.level_label || a.role_label || a.role}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Tipologia importo</Label>
                    <Select value={r.amount_type} onValueChange={(v) => updateRow(i, { amount_type: v })}>
                      <SelectTrigger data-testid={`select-type-${i}`} className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Importo fisso (€)</SelectItem>
                        <SelectItem value="percent">Percentuale del prezzo (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {r.amount_type === 'fixed' ? (
                    <div>
                      <Label className="text-xs">Importo (€)</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={r.amount_fixed}
                        onChange={(e) => updateRow(i, { amount_fixed: e.target.value })}
                        placeholder="Es. 5000"
                        className="mt-1 tabular-nums"
                        data-testid={`input-fixed-${i}`}
                      />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs">Percentuale (%)</Label>
                      <Input
                        type="number" step="0.01" min="0" max="100"
                        value={r.amount_percent}
                        onChange={(e) => updateRow(i, { amount_percent: e.target.value })}
                        placeholder="Es. 5"
                        className="mt-1 tabular-nums"
                        data-testid={`input-pct-${i}`}
                      />
                      {pp && Number(r.amount_percent) > 0 && (
                        <div className="text-[11px] text-[var(--gp-gold)] mt-0.5">
                          = {fmtEUR(previewAmount(r))} su {fmtEUR(paidAmount)}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <Label className="text-xs">Data sblocco fattura</Label>
                    <Input
                      type="date"
                      value={r.unlock_date}
                      onChange={(e) => updateRow(i, { unlock_date: e.target.value })}
                      className="mt-1"
                      data-testid={`input-date-${i}`}
                    />
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Data in cui l&apos;agente può emettere la fattura
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <Label className="text-xs">Note (opzionale)</Label>
                    <Textarea
                      value={r.notes}
                      onChange={(e) => updateRow(i, { notes: e.target.value })}
                      className="mt-1 min-h-[60px]"
                      placeholder="Es. split 60/40 con senior…"
                      data-testid={`input-notes-${i}`}
                    />
                  </div>
                </div>

                {pp && (
                  <div className="bg-[var(--gp-gold)]/8 border border-[var(--gp-gold)]/30 rounded-md p-2 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Importo commissione</span>
                    <span className="font-serif text-lg text-[var(--gp-gold)] tabular-nums">{fmtEUR(previewAmount(r))}</span>
                  </div>
                )}
              </div>
            ))}

            {!isEdit && (
              <Button variant="outline" onClick={addRow} className="w-full" data-testid="btn-add-split">
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Aggiungi altro agente (split)
              </Button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button onClick={onSubmit} disabled={!canSubmit || saving} data-testid="btn-save-commission">
            {saving ? 'Salvataggio…' : isEdit ? 'Salva modifiche' : `Crea ${rows.length > 1 ? `${rows.length} commissioni` : 'commissione'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Export CSV / PDF buttons ─────────────────────────────────────────────
export function ExportButtons({ variant = 'admin', filters = {} }) {
  const [downloading, setDownloading] = useState(null);

  const doDownload = async (format) => {
    setDownloading(format);
    try {
      const path = variant === 'admin'
        ? `/admin/agent-commissions/export.${format}`
        : `/agent-commissions/mine/export.${format}`;
      const resp = await api.get(path, { params: filters, responseType: 'blob' });
      const contentType = format === 'pdf' ? 'application/pdf' : 'text/csv';
      const blob = new Blob([resp.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // deriva filename dal Content-Disposition, o fallback
      const cd = resp.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      a.download = m ? m[1] : `commissioni_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} completato`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Errore export ${format.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="inline-flex items-center gap-1.5" data-testid="export-buttons">
      <Button
        variant="outline"
        className="h-10"
        onClick={() => doDownload('csv')}
        disabled={downloading !== null}
        data-testid="btn-export-csv"
      >
        <FileDown className="w-4 h-4 mr-1.5" />
        {downloading === 'csv' ? 'Export…' : 'CSV'}
      </Button>
      <Button
        variant="outline"
        className="h-10"
        onClick={() => doDownload('pdf')}
        disabled={downloading !== null}
        data-testid="btn-export-pdf"
      >
        <Download className="w-4 h-4 mr-1.5" />
        {downloading === 'pdf' ? 'Export…' : 'PDF'}
      </Button>
    </div>
  );
}
